import API from "serviceshift-ui/api-client";

import CONSTANTS from "@/Constants.js";
import {
  FailedUpload,
  MediaFile,
  UploadPayload,
  UploadProgress
} from "@/typings/media-upload";
import store from "@/vuex/store";

const {
  PHOTO_UPLOAD_SOURCE,
  PHOTO_MAX_SIZE,
  DOCUMENT_UPLOAD_SOURCE,
  DOCUMENT_MAX_SIZE
} = CONSTANTS;

export type MediaType = "photo" | "document";

interface MediaUploadConfig {
  mediaType: MediaType;
  link: {
    href: string;
    method?: string;
  };
  files: MediaFile[];
  uploadSource?: string;
  maxSize?: number;
}

/**
 * Consolidated upload class for photos and documents.
 * Both use direct S3 POST uploads (unlike videos which use multipart chunked uploads).
 */
export default class MediaUpload<T = unknown> {
  mediaType: MediaType;
  uploadSource: string;
  maxSize: number;
  id: string;
  success: number;
  failures: number;
  files: MediaFile[] = [];
  uploadPayloads: UploadPayload[] = [];
  link: {
    href: string;
    method?: string;
  };
  onProgress: ((progress: UploadProgress) => void) | null = null;
  cancelled = false;
  cancelSource = API.axios.CancelToken.source();
  failedUploads: FailedUpload[] = [];
  uploadedItems: T[] = [];

  constructor(config: MediaUploadConfig) {
    this.mediaType = config.mediaType;
    this.id = `${Date.now()}${Math.floor(Math.random() * 100000)}`;
    this.success = 0;
    this.failures = 0;
    this.link = config.link || { href: "" };
    this.files = config.files || [];

    // Set defaults based on media type
    if (this.mediaType === "photo") {
      this.uploadSource = config.uploadSource || PHOTO_UPLOAD_SOURCE;
      this.maxSize = config.maxSize || PHOTO_MAX_SIZE;
    } else {
      this.uploadSource = config.uploadSource || DOCUMENT_UPLOAD_SOURCE;
      this.maxSize = config.maxSize || DOCUMENT_MAX_SIZE;
    }
  }

  async uploadAll(): Promise<void> {
    if (this.link && !this.cancelled) {
      this.resetUploader();
      this.uploadPayloads = await this.getUploadPayloads();
      const promises: Promise<void>[] = [];
      this.uploadPayloads.forEach((payload) =>
        promises.push(this.upload(payload))
      );
      await Promise.all(promises);
      await this.saveMedia(this.uploadPayloads);
    }
  }

  async upload(payload: UploadPayload): Promise<void> {
    if (this.cancelled) return;

    if (payload.mediaFile.file.size > this.maxSize) {
      this.logFailure(payload, "too_large");
      return;
    }

    // Generate POST body formdata
    // includes s3 pre-signed url params and file
    const formData = new FormData();
    Object.keys(payload.parameters).forEach((key) => {
      formData.append(
        key,
        payload.parameters[key as keyof typeof payload.parameters]
      );
    });
    formData.append("file", payload.mediaFile.file);

    try {
      await API.makeRequest(payload.server_url, {
        method: "POST",
        data: formData,
        headers: {
          ignoreError: true
        },
        cancelToken: this.cancelSource.token
      });
      payload.success = true;
    } catch (err: unknown) {
      const error = err as { message?: string };
      if (error?.message !== "User cancelled upload") {
        console.error(err);
      }
      this.logFailure(payload, "upload_error");
    }
    payload.attempts = 1;
    this.updateProgress();
  }

  saveMedia(payloads: UploadPayload[]): Promise<(T | void)[]> {
    const uploads: Promise<T | void>[] = [];
    if (!this.cancelled) {
      payloads
        .filter((payload) => payload.success)
        .forEach((payload) => {
          const action =
            this.mediaType === "photo"
              ? "completePhotoUpload"
              : "completeDocumentUpload";

          uploads.push(
            store
              .dispatch(action, {
                url: payload.success_url,
                source: this.uploadSource,
                filename: payload.mediaFile.fileName
              })
              .then((response: { data: T }) => {
                this.success = this.success + 1;
                if (response?.data) {
                  this.uploadedItems.push(response.data);
                }
                return response?.data;
              })
              .catch(() => this.logFailure(payload, "upload_error"))
              .finally(() => this.updateProgress())
          );
        });
    }
    return Promise.all(uploads);
  }

  cancel(): void {
    this.cancelled = true;
    // Cancels all outstanding web requests
    if (this.cancelSource) {
      this.cancelSource.cancel("User cancelled upload");
    }
  }

  get extensions(): string[] {
    return this.files.map((file) =>
      file.fileName.substring(
        file.fileName.lastIndexOf(".") + 1,
        file.fileName.length
      )
    );
  }

  get size(): number {
    return this.files.length;
  }

  private async getUploadPayloads(): Promise<UploadPayload[]> {
    if (this.cancelled) return [];

    const action =
      this.mediaType === "photo"
        ? "reservePhotoUploads"
        : "reserveDocumentUploads";

    const payloads = await store.dispatch(action, {
      url: this.link.href,
      method: this.link.method,
      extensions: this.extensions
    });

    return payloads.map(
      (
        payload: Omit<UploadPayload, "mediaFile" | "success" | "attempts">,
        i: number
      ) => ({
        ...payload,
        mediaFile: this.files[i],
        success: false,
        attempts: 0
      })
    );
  }

  private logFailure(
    payload: UploadPayload,
    reason: FailedUpload["reason"]
  ): void {
    this.failures = this.failures + 1;
    payload.success = false;
    this.failedUploads.push({
      ...payload.mediaFile,
      reason
    });
  }

  private resetUploader(): void {
    this.cancelled = false;
    this.failedUploads = [];
    this.success = 0;
    this.failures = 0;
    this.uploadPayloads = [];
    this.uploadedItems = [];
    this.cancelSource = API.axios.CancelToken.source();
  }

  private updateProgress(): void {
    if (this.onProgress) {
      const completed =
        this.uploadPayloads.filter((payload) => payload.attempts > 0).length +
        this.success +
        this.failures;
      this.onProgress({
        progress: completed / (this.uploadPayloads.length * 2),
        uploads: this.uploadPayloads
      });
    }
  }
}
